// ─────────────────────────────────────────────────────────────────────────────
// TourContext — global tour state.
//
// Holds which flow is active and the current step index. The actual driving of
// the spotlight/popover and step advancement lives in TourRunner; this context
// is just the shared state + the controls (start / stop / next / set step).
// ─────────────────────────────────────────────────────────────────────────────
import React, { createContext, useContext, useState, useCallback } from 'react';
import { TOUR_FLOWS } from './tourFlows';

const TourContext = createContext(null);

export function TourProvider({ children }) {
  const [activeFlowId, setActiveFlowId] = useState(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [launcherOpen, setLauncherOpen] = useState(false);
  // Signals TourRunner to force-navigate to step 0's route even if already there.
  // Incremented on each startTour call so the runner effect always fires.
  const [startSignal, setStartSignal] = useState(0);

  const startTour = useCallback((flowId) => {
    if (!TOUR_FLOWS[flowId]) return;
    setLauncherOpen(false);
    setStepIndex(0);
    setActiveFlowId(flowId);
    setStartSignal(s => s + 1);
  }, []);

  const stopTour = useCallback(() => {
    setActiveFlowId(null);
    setStepIndex(0);
  }, []);

  const goToStep = useCallback((idx) => setStepIndex(idx), []);

  // Advance to the next step. Pure single-setter update. If we're already on
  // the last step, end the tour. We read the flow via the functional updater's
  // closure over activeFlowId is unavailable here, so resolve it from a ref-free
  // lookup using the current activeFlowId captured in the dep array.
  const nextStep = useCallback(() => {
    const flow = activeFlowId ? TOUR_FLOWS[activeFlowId] : null;
    if (!flow) return;
    setStepIndex((i) => {
      const next = i + 1;
      if (next >= flow.steps.length) {
        // Past the last step → end the tour on the next tick.
        setActiveFlowId(null);
        return 0;
      }
      return next;
    });
  }, [activeFlowId]);

  const value = {
    activeFlowId,
    activeFlow: activeFlowId ? TOUR_FLOWS[activeFlowId] : null,
    stepIndex,
    isRunning: !!activeFlowId,
    launcherOpen,
    startSignal,
    openLauncher: () => setLauncherOpen(true),
    closeLauncher: () => setLauncherOpen(false),
    startTour,
    stopTour,
    nextStep,
    goToStep,
  };

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
}

export function useTour() {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error('useTour must be used within a TourProvider');
  return ctx;
}
